import { FDRStatusQuery, FieldQueryOptions } from '@cibo/core'
import { useLocalState } from '@cibo/ui'
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBatchedFields } from '../../../queries'
import { useFieldSelectionContext } from './FieldSelectionContext'
import { FieldSelectionProvider } from './FieldSelectionProvider'
import { HideFilters, PaginatedFieldsContext } from './PaginatedFieldsContext'

export const PAGE_SIZES = [10, 25, 50]

type PaginatedFieldsProviderProps = {
  hideFilters?: HideFilters
  children?: ReactNode
  persistKey?: string
}

const parseJsonFromSearchParams = (searchParam: string) => {
  if (!searchParam) return undefined
  try {
    return JSON.parse(searchParam)
  } catch (e) {
    console.error(`Error parsing search param ${searchParam}:`, e)
    return undefined
  }
}

const PaginatedFieldsProviderBase = ({
  children,
  hideFilters = {},
  persistKey,
}: PropsWithChildren<PaginatedFieldsProviderProps>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState<FieldQueryOptions>({})
  const [baseFilters, setBaseFilters] = useState<FieldQueryOptions>({})
  const [stateFilters, setStateFilters] = useLocalState<FieldQueryOptions>({}, persistKey)

  useEffect(() => {
    !!searchParams.get('page') && searchParams.set('page', '0')
    !!searchParams.get('pageSize') && searchParams.set('pageSize', '10')

    setFilters({
      owner: searchParams.get('owner') as string,
      fdrStatusAnyOf: parseJsonFromSearchParams(
        searchParams.get('fdrStatusAnyOf') as string
      ) as FDRStatusQuery[],
      ...stateFilters,
    })
    setSearchParams(searchParams)
  }, [])

  useEffect(() => {
    setFilters({ owner: searchParams.get('owner') as string, ...stateFilters })
    !!stateFilters.owner && searchParams.set('owner', stateFilters.owner as string)
    !!stateFilters.fdrStatusAnyOf &&
      searchParams.set('fdrStatusAnyOf', JSON.stringify(stateFilters.fdrStatusAnyOf))
    setSearchParams(searchParams)
  }, [stateFilters])

  const onSetFilters = (newFilters: FieldQueryOptions) => {
    setStateFilters(newFilters)
    setFilters(newFilters)
    if (newFilters.owner) {
      searchParams.set('owner', newFilters.owner.toString())
    }
    !newFilters.owner && searchParams.delete('owner')
    setSearchParams(searchParams)
  }

  const { selectedRowIds } = useFieldSelectionContext()

  const fields = useBatchedFields(selectedRowIds as string[])

  return (
    <PaginatedFieldsContext.Provider
      value={{
        filters,
        setFilters: onSetFilters,
        hideFilters,
        baseFilters,
        setBaseFilters,

        isReady: !fields.isPending,

        page: searchParams.get('page') ? parseInt(searchParams.get('page') as string, 10) : 0,
        setPage: (newPage: number) => {
          searchParams.set('page', newPage.toString())
          setSearchParams(searchParams)
        },

        pageSize: searchParams.get('pageSize')
          ? parseInt(searchParams.get('pageSize') as string, 10)
          : PAGE_SIZES[0],
        setPageSize: (newPageSize: number) => {
          searchParams.set('pageSize', newPageSize.toString())
          setSearchParams(searchParams)
        },
      }}
    >
      {children}
    </PaginatedFieldsContext.Provider>
  )
}

export const PaginatedFieldsProvider = (props: PaginatedFieldsProviderProps) => {
  return (
    <FieldSelectionProvider>
      <PaginatedFieldsProviderBase {...props} />
    </FieldSelectionProvider>
  )
}
